import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiMode';
import { HelpCircle, Check, Trash2, Plus, Globe } from 'lucide-react';

const LANGS = [
  { code: 'it', label: 'Italiano 🇮🇹' },
  { code: 'fr', label: 'Français 🇫🇷' },
  { code: 'es', label: 'Español 🇪🇸' },
  { code: 'de', label: 'Deutsch 🇩🇪' },
];

const tdS = { padding: '0.7rem 0.9rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.865rem', verticalAlign: 'top' };
const thS = { ...tdS, background: '#f8fafc', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' };

export default function AdminFAQSection({ token }) {
  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [answering, setAnswering] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ question: '', answer: '' });
  const [savingNew, setSavingNew] = useState(false);

  // Translation modal state
  const [translating, setTranslating] = useState(null); // faq being translated
  const [activeLang, setActiveLang] = useState('it');
  const [transFields, setTransFields] = useState({}); // { it: { question, answer }, fr: {...}, ... }
  const [savingLang, setSavingLang] = useState(null);
  const [deletingLang, setDeletingLang] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/api/partner/admin/faq');
      setFaqs(r.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore caricamento FAQ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const filtered = faqs.filter(f => {
    if (filter === 'all') return true;
    return f.status === filter;
  });

  const pending = faqs.filter(f => f.status === 'pending').length;
  const published = faqs.filter(f => f.status === 'published').length;

  const openAnswer = (faq) => {
    setAnswering(faq);
    setAnswerText(faq.answer || '');
  };

  const openTranslations = (faq) => {
    // Build transFields from existing translations
    const fields = {};
    for (const lang of LANGS) {
      const t = faq.translations?.[lang.code];
      fields[lang.code] = {
        question: t?.question || '',
        answer: t?.answer || '',
      };
    }
    setTransFields(fields);
    setActiveLang('it');
    setTranslating(faq);
  };

  const handlePublish = async () => {
    if (!answerText.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/api/partner/admin/faq/${answering.id}`, { answer: answerText, status: 'published' });
      setAnswering(null);
      setAnswerText('');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore pubblicazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm('Eliminare questa FAQ?')) return;
    try {
      await api.delete(`/api/partner/admin/faq/${faq.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore eliminazione');
    }
  };

  const handleUnpublish = async (faq) => {
    try {
      await api.patch(`/api/partner/admin/faq/${faq.id}`, { answer: faq.answer, status: 'pending' });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore');
    }
  };

  const handleCreateFAQ = async () => {
    if (!newForm.question.trim() || !newForm.answer.trim()) return;
    setSavingNew(true);
    try {
      const created = await api.post('/api/partner/faq/question', { question: newForm.question });
      await api.patch(`/api/partner/admin/faq/${created.data.id}`, {
        answer: newForm.answer,
        status: 'published'
      });
      setShowNewForm(false);
      setNewForm({ question: '', answer: '' });
      setFilter('published');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore creazione FAQ');
    } finally {
      setSavingNew(false);
    }
  };

  const handleSaveTranslation = async (lang) => {
    const fields = transFields[lang] || {};
    if (!fields.question?.trim()) {
      alert('La domanda è obbligatoria per salvare la traduzione.');
      return;
    }
    setSavingLang(lang);
    try {
      await api.put(`/api/partner/admin/faq/${translating.id}/translations/${lang}`, {
        question: fields.question.trim(),
        answer: fields.answer?.trim() || null,
      });
      await load();
      // Update translating faq with fresh data
      setTranslating(prev => {
        const updated = faqs.find(f => f.id === prev.id);
        if (updated) {
          const freshT = { ...(updated.translations || {}), [lang]: { question: fields.question.trim(), answer: fields.answer?.trim() || null } };
          return { ...updated, translations: freshT };
        }
        return prev;
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Errore salvataggio traduzione');
    } finally {
      setSavingLang(null);
    }
  };

  const handleDeleteTranslation = async (lang) => {
    if (!window.confirm(`Eliminare la traduzione in ${lang.toUpperCase()}?`)) return;
    setDeletingLang(lang);
    try {
      await api.delete(`/api/partner/admin/faq/${translating.id}/translations/${lang}`);
      setTransFields(prev => ({ ...prev, [lang]: { question: '', answer: '' } }));
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore eliminazione traduzione');
    } finally {
      setDeletingLang(null);
    }
  };

  // Count languages with translation for a FAQ
  const translationCount = (faq) => Object.keys(faq.translations || {}).length;

  return (
    <div>
      {/* New FAQ modal */}
      {showNewForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: '#1e3a8a' }}>Nuova FAQ</h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>
                Domanda <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={newForm.question}
                onChange={e => setNewForm(f => ({ ...f, question: e.target.value }))}
                placeholder="Es. Come si ottiene l'autorizzazione BVLOS?"
                style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>
                Risposta <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={newForm.answer}
                onChange={e => setNewForm(f => ({ ...f, answer: e.target.value }))}
                rows={5}
                placeholder="Scrivi la risposta..."
                style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowNewForm(false); setNewForm({ question: '', answer: '' }); }}
                style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontWeight: 600, cursor: 'pointer' }}
              >
                Annulla
              </button>
              <button
                onClick={handleCreateFAQ}
                disabled={savingNew || !newForm.question.trim() || !newForm.answer.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.25rem', borderRadius: 8, border: 'none', background: '#16a34a', color: 'white', fontWeight: 700, cursor: 'pointer', opacity: (!newForm.question.trim() || !newForm.answer.trim()) ? 0.6 : 1 }}
              >
                <Check size={15} /> {savingNew ? 'Salvataggio...' : 'Pubblica FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Answer modal */}
      {answering && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 0.75rem', color: '#1e3a8a' }}>Rispondi e pubblica</h3>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', color: '#334155', fontSize: '0.9rem' }}>
              <strong>Domanda:</strong> {answering.question}
            </div>
            {answering.submitted_by_name && (
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.75rem' }}>
                Inviata da: {answering.submitted_by_name} ({answering.submitted_by_email}) — {fmt(answering.created_at)}
              </div>
            )}
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>
              Risposta <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={answerText}
              onChange={e => setAnswerText(e.target.value)}
              rows={5}
              placeholder="Scrivi la risposta..."
              style={{ width: '100%', padding: '0.65rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setAnswering(null)} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontWeight: 600, cursor: 'pointer' }}>
                Annulla
              </button>
              <button
                onClick={handlePublish}
                disabled={saving || !answerText.trim()}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.25rem', borderRadius: 8, border: 'none', background: '#16a34a', color: 'white', fontWeight: 700, cursor: 'pointer' }}
              >
                <Check size={15} /> {saving ? 'Pubblicazione...' : 'Pubblica FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Translations modal */}
      {translating && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.35rem', color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Globe size={18} /> Gestisci traduzioni
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                    La versione originale (EN) è modificabile dalla colonna "Modifica". Qui gestisci le traduzioni.
                  </p>
                </div>
                <button onClick={() => setTranslating(null)} style={{ border: 'none', background: 'none', fontSize: '1.4rem', color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              {/* Original EN preview */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', marginTop: '0.85rem', fontSize: '0.85rem', color: '#334155' }}>
                <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>🇬🇧 EN — {translating.question}</div>
                <div style={{ color: '#64748b' }}>{translating.answer?.slice(0, 120)}{translating.answer?.length > 120 ? '…' : ''}</div>
              </div>
            </div>

            {/* Language tabs */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
              {LANGS.map(lang => {
                const hasTranslation = !!(translating.translations?.[lang.code]?.question);
                return (
                  <button
                    key={lang.code}
                    onClick={() => setActiveLang(lang.code)}
                    style={{
                      flex: 1, padding: '0.65rem 0.5rem', border: 'none', cursor: 'pointer',
                      background: activeLang === lang.code ? 'white' : 'transparent',
                      borderBottom: activeLang === lang.code ? '2px solid #1d4ed8' : '2px solid transparent',
                      fontWeight: activeLang === lang.code ? 700 : 500,
                      color: activeLang === lang.code ? '#1d4ed8' : '#64748b',
                      fontSize: '0.83rem', transition: 'all 0.15s',
                      position: 'relative',
                    }}
                  >
                    {lang.label}
                    {hasTranslation && (
                      <span style={{ display: 'inline-block', width: 7, height: 7, background: '#16a34a', borderRadius: '50%', marginLeft: 5, verticalAlign: 'middle' }} title="Traduzione presente" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div style={{ padding: '1.25rem 1.5rem' }}>
              {LANGS.filter(l => l.code === activeLang).map(lang => (
                <div key={lang.code}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>
                      Domanda in {lang.label} <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={transFields[lang.code]?.question || ''}
                      onChange={e => setTransFields(prev => ({ ...prev, [lang.code]: { ...prev[lang.code], question: e.target.value } }))}
                      placeholder={`Domanda in ${lang.label}...`}
                      style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>
                      Risposta in {lang.label}
                    </label>
                    <textarea
                      value={transFields[lang.code]?.answer || ''}
                      onChange={e => setTransFields(prev => ({ ...prev, [lang.code]: { ...prev[lang.code], answer: e.target.value } }))}
                      rows={5}
                      placeholder={`Risposta in ${lang.label}...`}
                      style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      {translating.translations?.[lang.code] && (
                        <button
                          onClick={() => handleDeleteTranslation(lang.code)}
                          disabled={deletingLang === lang.code}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}
                        >
                          <Trash2 size={13} /> {deletingLang === lang.code ? 'Eliminando...' : 'Elimina traduzione'}
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleSaveTranslation(lang.code)}
                      disabled={savingLang === lang.code || !transFields[lang.code]?.question?.trim()}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.25rem', borderRadius: 8, border: 'none', background: '#1d4ed8', color: 'white', fontWeight: 700, cursor: 'pointer', opacity: !transFields[lang.code]?.question?.trim() ? 0.6 : 1 }}
                    >
                      <Check size={15} /> {savingLang === lang.code ? 'Salvataggio...' : 'Salva traduzione'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e3a8a', fontWeight: 800 }}>Gestione FAQ</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.87rem' }}>Rispondi alle domande inviate dagli utenti partner e pubblicale.</p>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem' }}
        >
          <Plus size={15} /> Nuova FAQ
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>{error}</div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1rem' }}>
        {[
          { key: 'pending', label: `⏳ In attesa (${pending})` },
          { key: 'published', label: `✅ Pubblicate (${published})` },
          { key: 'all', label: `Tutte (${faqs.length})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            style={{ padding: '0.4rem 0.9rem', borderRadius: 999, border: '1px solid #cbd5e1', background: filter === tab.key ? '#1d4ed8' : 'white', color: filter === tab.key ? 'white' : '#334155', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#64748b' }}>Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '2.5rem', textAlign: 'center', color: '#64748b' }}>
          <HelpCircle size={36} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
          <p>Nessuna {filter === 'pending' ? 'domanda in attesa' : filter === 'published' ? 'FAQ pubblicata' : 'FAQ'}.</p>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Domanda', 'Inviata da', 'Data', 'Stato', 'Traduzioni', 'Azioni'].map(h => <th key={h} style={thS}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map(faq => (
                <tr key={faq.id}>
                  <td style={{ ...tdS, maxWidth: 300 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: faq.answer ? '0.3rem' : 0 }}>{faq.question}</div>
                    {faq.answer && (
                      <div style={{ fontSize: '0.8rem', color: '#475569', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '0.4rem 0.6rem', marginTop: '0.35rem' }}>
                        <strong>R:</strong> {faq.answer.slice(0, 120)}{faq.answer.length > 120 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td style={tdS}>{faq.submitted_by_name || faq.submitted_by_email || 'Admin'}</td>
                  <td style={tdS}>{fmt(faq.created_at)}</td>
                  <td style={tdS}>
                    <span style={{
                      padding: '0.2rem 0.6rem', borderRadius: 999, fontWeight: 700, fontSize: '0.75rem',
                      background: faq.status === 'published' ? '#dcfce7' : '#fef9c3',
                      color: faq.status === 'published' ? '#15803d' : '#854d0e'
                    }}>
                      {faq.status === 'published' ? 'Pubblicata' : 'In attesa'}
                    </span>
                  </td>
                  <td style={tdS}>
                    {faq.status === 'published' ? (
                      <button
                        onClick={() => openTranslations(faq)}
                        title="Gestisci traduzioni"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', borderRadius: 7, padding: '0.3rem 0.55rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}
                      >
                        <Globe size={12} /> {translationCount(faq)}/4
                      </button>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td style={tdS}>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openAnswer(faq)}
                        style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: 7, padding: '0.3rem 0.55rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}
                      >
                        {faq.status === 'published' ? 'Modifica' : 'Rispondi'}
                      </button>
                      {faq.status === 'published' && (
                        <button
                          onClick={() => handleUnpublish(faq)}
                          style={{ border: '1px solid #fde68a', background: '#fefce8', color: '#854d0e', borderRadius: 7, padding: '0.3rem 0.55rem', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}
                        >
                          Sospendi
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(faq)}
                        style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', borderRadius: 7, padding: '0.3rem 0.45rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
